import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import TableHead from '../../../../components/Table/TableHead';
import TableRow from '../../../../components/Table/TableRow';
import PermissionsGate from '../../../../components/PermissionsGate/PermissionsGate';
import { permissions } from '../../../../common/permissionGroup';
import TableHeadData from '../../../../components/Table/TableHeadData';
import TableBody from '../../../../components/Table/TableBody';
import TableData from '../../../../components/Table/TableData';
import Button from '../../../../components/Button/Button';
import ButtonTypes from '../../../../constants/buttonTypes';
import Text from '../../../../components/Text/Text';
import Badge from '../../../../components/Badge/Badge';
import { orderStatusInfo } from '../../../../constants/orderedPartsStatus';
import prepareProviderLink from '../../../../helpers/general';
import EmptyTable from '../../../../components/Table/EmptyTable';
import Table from '../../../../components/Table/Table';
import { coAcceptanceTable } from '../../../../constants/coAcceptanceTable';
import { setApplicationState } from '../../../../actions/application/application';
import toggleArrayItem from '../../../../helpers/toggleArrayItem';

const tableCellClassName = 'min-w-max whitespace-nowrap px-[9px] py-0 text-center';
const actionButtonClassName = 'p-[5px] text-[10px]';

const AcceptanceTable = ({ data, onDelivered, loading }) => {
  const dispatch = useDispatch();
  const {
    application: { coAcceptanceTableSetting, selectedRow },
  } = useSelector((state) => ({
    application: state?.application,
  }), shallowEqual);

  const getColumn = () => {
    if (coAcceptanceTableSetting?.length) {
      return coAcceptanceTable.filter(({ source }) => coAcceptanceTableSetting.includes(source));
    }
    return coAcceptanceTable;
  };

  const selectRow = (id) => {
    dispatch(setApplicationState({
      selectedRow: toggleArrayItem(selectedRow, id)
    }));
  };

  return (
    <Table loading={loading}>
      <TableHead>
        <TableRow bordered>
          <PermissionsGate can={permissions.COW_UPDATE}>
            <TableHeadData />
          </PermissionsGate>
          {
            getColumn().map(({ title, source, sortable }) => (
              <TableHeadData
                key={source}
                sortable={sortable}
                source={source}
                onSort={() => {}}
              >
                {title}
              </TableHeadData>
            ))
          }
        </TableRow>
      </TableHead>
      <TableBody>
        {data?.length ? data.map(({
            _id,
            ordered_part_id: id,
            ordered_part_po: po,
            ordered_part_tracking: tracking,
            ordered_part_brand: brand,
            ordered_part_mpn: mpn,
            ordered_part_qty_in_order: qtyInOrder,
            ordered_part_status: partStatus,
            ordered_part_qty_delivered: delivered,
            ordered_part_est_delivery_date: estDate,
            ordered_part_included: included,
          }, index) => {
            const trackingArr = tracking?.split(',') || [];
            return (
              <TableRow
                key={`tr_${_id}`}
                bordered={index < data.length - 1}
                selected={selectedRow.includes(_id)}
                onClick={() => selectRow(_id)}
              >
                <PermissionsGate can={permissions.COW_UPDATE}>
                  <TableData>
                    <div className="flex justify-center">
                      <Button
                        type={ButtonTypes.INFO}
                        text="delivered"
                        containerStyle={actionButtonClassName}
                        onClick={() => onDelivered(data[index])}
                      />
                    </div>
                  </TableData>
                </PermissionsGate>
                {
                  coAcceptanceTableSetting?.includes('ordered_part_id')
                  && (
                  <TableData className={tableCellClassName}>
                    <a
                      href={`/coilovers/ordered-parts/?query=${po}`}
                      target="_blank"
                      className="globalLink"
                      rel="noreferrer"
                    >
                      {id || ''}
                    </a>
                  </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting?.includes('ordered_part_po')
                  && (
                    <TableData className={tableCellClassName}>
                      <Text text={po || ''} bold styleContainer="justify-center" />
                    </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting.includes('ordered_part_status')
                  && (
                    <TableData className={tableCellClassName}>
                      {partStatus
                        && (
                          <div className="flex justify-center">
                            <Badge
                              text={partStatus}
                              type={orderStatusInfo?.[partStatus]?.state}
                            />
                          </div>
                        )}
                    </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting?.includes('ordered_part_tracking')
                  && (
                    <TableData>
                      {trackingArr.map((trac, i) => (
                        <Fragment key={trac}>
                          <a
                            href={prepareProviderLink(trac)}
                            className="globalLink"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {trac}
                          </a>
                          {i !== trackingArr.length - 1 && ', '}
                        </Fragment>
                      ))}
                    </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting.includes('ordered_part_est_delivery_date')
                  && (
                    <TableData className={tableCellClassName}>
                      <div className="flex justify-center">
                        {estDate && (
                        <Badge
                          type={ButtonTypes.LIGHT}
                          text={DateTime.fromFormat(estDate, 'yyyy-MM-dd', { zone: 'utc' })
                            .setLocale().toFormat('DD')}
                        />
                      )}
                      </div>
                    </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting?.includes('ordered_part_brand')
                  && (
                    <TableData className={tableCellClassName}>
                      {brand}
                    </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting?.includes('ordered_part_mpn')
                  && (
                    <TableData className={tableCellClassName}>
                      <Text text={mpn || ''} bold styleContainer="justify-center" />
                    </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting?.includes('ordered_part_qty_delivered')
                  && (
                    <TableData className={tableCellClassName}>
                      {delivered || 0}
                    </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting?.includes('ordered_part_qty_in_order')
                  && (
                    <TableData className={tableCellClassName}>
                      {qtyInOrder}
                    </TableData>
                  )
                }
                {
                  coAcceptanceTableSetting?.includes('ordered_part_included')
                  && (
                    <TableData className={tableCellClassName}>
                      {included}
                    </TableData>
                  )
                }
              </TableRow>
            );
          })
          : <EmptyTable colSpan={coAcceptanceTable.length + 1} text="No any data find" />}
      </TableBody>
    </Table>
  );
};

AcceptanceTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  onDelivered: PropTypes.func,
  loading: PropTypes.bool,
};

AcceptanceTable.defaultProps = {
  data: [],
  onDelivered: () => {},
  loading: false,
};

export default AcceptanceTable;
